import { Play, Sparkles } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MEDITATIONS, CONTACT } from "@/data";

function Embed({ id, title }) {
  return (
    <iframe
      className="w-full h-full"
      src={`https://www.youtube.com/embed/${id}`}
      title={title}
      loading="lazy"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}

function FeaturedVideo({ item }) {
  return (
    <Reveal>
      <div data-testid={`meditation-featured-${item.id}`} className="grid lg:grid-cols-12 gap-8 items-center mb-12">
        <div className="lg:col-span-8">
          <div className="relative overflow-hidden border border-[rgba(200,149,54,0.4)] bg-black aspect-video shadow-[0_30px_70px_-40px_rgba(197,160,89,0.7)]">
            <Embed id={item.id} title={item.title} />
          </div>
        </div>
        <div className="lg:col-span-4">
          <span className="inline-flex items-center gap-2 font-sans-ui text-[0.6rem] tracking-[0.2em] uppercase px-3 py-1.5 rounded-full bg-[var(--gold)] text-[var(--noir)]">
            <Sparkles size={12} /> Featured Practice
          </span>
          <h3 className="font-serif-display text-3xl md:text-4xl leading-tight mt-5">{item.title}</h3>
          <p className="font-sans-ui text-sm text-[var(--muted-ink)] leading-relaxed font-light mt-4">{item.desc}</p>
        </div>
      </div>
    </Reveal>
  );
}

function VideoGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-8">
      {items.map((m, i) => (
        <Reveal key={m.id} delay={(i % 2) * 0.08}>
          <div data-testid={`meditation-${m.id}`} className="group">
            <div className="relative overflow-hidden border border-[rgba(200,149,54,0.3)] bg-black aspect-video">
              <Embed id={m.id} title={m.title} />
            </div>
            <h3 className="font-serif-display text-2xl mt-4 group-hover:text-[var(--gold)] transition-colors duration-300">{m.title}</h3>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

function Library({ items }) {
  const featured = items.find((v) => v.featured) || items[0];
  const rest = items.filter((v) => v.id !== featured.id);
  return (
    <>
      <FeaturedVideo item={featured} />
      <VideoGrid items={rest} />
    </>
  );
}

export default function Meditations() {
  return (
    <section id="meditations" data-testid="meditations-section" className="section-pad py-24 md:py-36">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div className="max-w-2xl">
          <Overline>YouTube Meditation Library</Overline>
          <Reveal>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6">
              Stillness, whenever you need it.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="shrink-0 flex flex-wrap gap-3">
            <a
              href={CONTACT.youtube}
              target="_blank"
              rel="noreferrer"
              data-testid="youtube-channel-btn"
              className="inline-flex items-center gap-2 font-sans-ui text-sm tracking-wide px-7 py-4 border border-[var(--ink)] rounded-full hover:bg-[var(--gold)] hover:text-[var(--noir)] transition-colors duration-400"
            >
              <Play size={15} /> Brahmsara
            </a>
            <a
              href={CONTACT.youtubeHindi}
              target="_blank"
              rel="noreferrer"
              data-testid="youtube-hindi-channel-btn"
              className="inline-flex items-center gap-2 font-sans-ui text-sm tracking-wide px-7 py-4 border border-[var(--ink)] rounded-full hover:bg-[var(--gold)] hover:text-[var(--noir)] transition-colors duration-400"
            >
              <Play size={15} /> Brahmsara Hindi
            </a>
          </div>
        </Reveal>
      </div>

      <Tabs defaultValue="english" className="w-full" data-testid="meditation-tabs">
        <TabsList className="bg-[var(--alt)] border border-[rgba(200,149,54,0.3)] rounded-full p-1 mb-10">
          <TabsTrigger value="english" data-testid="tab-english" className="rounded-full px-6 data-[state=active]:bg-[var(--gold)] data-[state=active]:text-[var(--noir)] font-sans-ui text-sm">
            English
          </TabsTrigger>
          <TabsTrigger value="hindi" data-testid="tab-hindi" className="rounded-full px-6 data-[state=active]:bg-[var(--gold)] data-[state=active]:text-[var(--noir)] font-sans-ui text-sm">
            Hindi
          </TabsTrigger>
        </TabsList>
        <TabsContent value="english">
          <Library items={MEDITATIONS.english} />
        </TabsContent>
        <TabsContent value="hindi">
          <Library items={MEDITATIONS.hindi} />
        </TabsContent>
      </Tabs>
    </section>
  );
}
